import { computed, inject, toValue, type MaybeRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type {
  SitePrivateConfig,
  PaymentConfig,
  BasePrivateConfig,
  StationPrivateConfig,
} from '@centric-os/types';
import { isNil, cloneDeep, isEmpty } from 'lodash-es';
import {
  useAppConfigStore,
  useSiteConfigStore,
  useStationConfigStore,
} from '@centric-os/sites/src/routes/stores';
import { PAYMENT_CONFIG_TYPES } from '../../../enums';
import { FREEDOM_PAY_PAYMENT_ID } from '../../../../../../constants';
import { paymentInfoContextKey } from '../../context';

const hasFreedomPayCredentials = (
  freedompay?: { freedompay_store_id?: string; freedompay_terminal_id?: string } | null,
): boolean => {
  return Boolean(freedompay && !isEmpty(freedompay));
};

const usePaymentInfo = (options?: { isCentricHub?: MaybeRef<boolean> }) => {
  const paymentInfoContext = inject(paymentInfoContextKey, null);
  const isCentricHub = computed(
    () => toValue(options?.isCentricHub) ?? paymentInfoContext?.isCentricHub.value ?? false,
  );
  // Set initial values for payment info form
  const initialValues = computed<Partial<PaymentConfig>>(() => {
    const stationConfigStore = useStationConfigStore();
    const { stationPrivateConfig } = storeToRefs(stationConfigStore);

    if (isCentricHub.value) {
      if (!hasPaymentConfig.value) {
        return {};
      }

      const centricHubFreedomPay = stationPrivateConfig.value?.keystore?.centric_hub?.freedompay;

      return {
        type: PAYMENT_CONFIG_TYPES.FREEDOM_PAY,
        freedompay: {
          freedompay_store_id: centricHubFreedomPay?.freedompay_store_id,
          freedompay_terminal_id: centricHubFreedomPay?.freedompay_terminal_id,
        },
      };
    }

    const { isMobileIntegrated } = useAppConfigStore();
    const siteConfigStore = useSiteConfigStore();
    const { sitePrivateConfig } = storeToRefs(siteConfigStore);

    const stationKeystorePaymentConfig = stationPrivateConfig.value?.keystore?.payment;

    let baseConfig: Partial<PaymentConfig> = {};
    if (!hasPaymentConfig.value) {
      return baseConfig;
    }
    if (stationKeystorePaymentConfig && !isEmpty(stationPrivateConfig.value?.payment)) {
      // If station has its own payment config, prioritize it
      // Can still use above assertion to determine if FreedomPay is available as it must be enabled on Site level
      baseConfig = getPaymentKeystoreConfig(stationPrivateConfig.value);
      return baseConfig;
    }

    if (!isMobileIntegrated) {
      // on station level fallback to site config only during integration init
      // Fallback to site level FreedomPay config if station doesn't have one
      // In some scenarios the site level config can be empty but the station level config is not
      baseConfig = getPaymentKeystoreConfig(sitePrivateConfig.value);
    }

    return baseConfig;
  });

  // Computed for payment config availability
  const hasPaymentConfig = computed<boolean>(() => {
    const stationConfigStore = useStationConfigStore();
    const { stationPrivateConfig } = storeToRefs(stationConfigStore);

    if (isCentricHub.value) {
      return hasFreedomPayCredentials(
        stationPrivateConfig.value?.keystore?.centric_hub?.freedompay,
      );
    }

    const { isMobileIntegrated } = useAppConfigStore();

    let keystorePaymentConfig;

    if (
      stationPrivateConfig.value?.keystore?.payment &&
      !isEmpty(stationPrivateConfig.value?.payment)
    ) {
      // If station has its own payment config, prioritize it
      keystorePaymentConfig = stationPrivateConfig.value?.keystore?.payment;
    } else if (!isMobileIntegrated) {
      const siteConfigStore = useSiteConfigStore();
      const { sitePrivateConfig } = storeToRefs(siteConfigStore);

      keystorePaymentConfig = sitePrivateConfig.value?.keystore?.payment;
    }

    const keystoreFreedomPay = keystorePaymentConfig?.freedompay ?? null;

    // We only have FreedomPay for now
    return Boolean(
      hasFreedomPayCredentials(keystoreFreedomPay) || keystorePaymentConfig?.is_encrypted,
    );
  });

  // Computed for payment config types
  const paymentConfigTypes = computed<{ label: string; value: string }[]>(() => {
    // Set localization
    const { t } = useI18n();

    const types = [
      {
        label: t('sites.singleSite.financialConfig.paymentInfo.form.types.freedomPay'),
        value: PAYMENT_CONFIG_TYPES.FREEDOM_PAY,
      },
    ];

    // Sort types by label alphabetically
    return types.toSorted((a, b) => a.label.localeCompare(b.label));
  });

  /**
   * Map form values to the private config payload
   * @param formValues - The form values to map
   * @param config - the existing config object (site or station)
   */
  const mapPrivateConfigPayload = (
    formValues: Partial<PaymentConfig>,
    config: BasePrivateConfig,
  ): SitePrivateConfig | StationPrivateConfig => {
    let payload = cloneDeep(config);

    if (
      !isNil(formValues.freedompay?.freedompay_store_id) &&
      !isNil(formValues.freedompay?.freedompay_terminal_id)
    ) {
      payload = {
        ...payload,
        keystore: {
          ...payload?.keystore,
          payment: {
            ...payload?.keystore?.payment,
            // Currently only FreedomPay is supported. If other payment types are added, this logic will need to be updated.
            type: PAYMENT_CONFIG_TYPES.FREEDOM_PAY,
            freedompay: {
              freedompay_store_id: formValues.freedompay?.freedompay_store_id,
              freedompay_terminal_id: formValues.freedompay?.freedompay_terminal_id,
            },
          },
        },
        // CDL-24814 - As per AP3, this payment object is added to stations with FP integrations for CC ordering
        payment: {
          ...payload?.payment,
          freedompay: {
            id: FREEDOM_PAY_PAYMENT_ID,
          },
          refund: false,
        },
      };

      // Prevent sending encrypted properties back to the server if the payload has valid FreedomPay data
      delete payload.keystore?.payment?.is_encrypted;
      delete payload.keystore?.payment?.data;
    } else if (formValues.is_encrypted) {
      payload = {
        ...payload,
        keystore: {
          ...payload?.keystore,
          payment: {
            ...payload?.keystore?.payment,
            is_encrypted: formValues.is_encrypted,
            data: formValues.data,
          },
        },
        payment: {
          ...payload?.payment,
          freedompay: {
            id: FREEDOM_PAY_PAYMENT_ID,
          },
          refund: false,
        },
      };
    }

    return payload;
  };

  // Function to map Centric Hub payment payload
  const mapCentricHubPaymentPayload = (formValues: Partial<PaymentConfig>, config: any): any => {
    let payload = cloneDeep(config);

    if (
      !isNil(formValues.freedompay?.freedompay_store_id) &&
      !isNil(formValues.freedompay?.freedompay_terminal_id)
    ) {
      payload = {
        ...payload,
        keystore: {
          ...payload?.keystore,
          centric_hub: {
            ...payload?.keystore?.centric_hub,
            freedompay: {
              freedompay_store_id: formValues.freedompay?.freedompay_store_id,
              freedompay_terminal_id: formValues.freedompay?.freedompay_terminal_id,
            },
          },
        },
      };
    }

    return payload;
  };

  const clearCentricHubPaymentPayload = (config: StationPrivateConfig): StationPrivateConfig => {
    const payload = cloneDeep(config);

    if (!payload?.keystore?.centric_hub?.freedompay) {
      return payload;
    }

    const centricHub = { ...payload.keystore.centric_hub };
    delete centricHub.freedompay;

    const keystore = { ...payload.keystore };
    if (isEmpty(centricHub)) {
      delete keystore.centric_hub;
    } else {
      keystore.centric_hub = centricHub;
    }

    payload.keystore = isEmpty(keystore) ? undefined : keystore;

    return payload;
  };

  /**
   * Extracts payment configuration from the private config, handling both encrypted and unencrypted cases
   *
   * @param {SitePrivateConfig} privateConfig - The site or station private configuration object
   * @returns {Partial<PaymentConfig>} The extracted payment configuration
   *
   * @remarks
   * This function handles two mutually exclusive payment configurations:
   * 1. FreedomPay configuration with store/terminal IDs
   * 2. Encrypted configuration with is_encrypted flag and encrypted data
   */
  const getPaymentKeystoreConfig = (privateConfig: BasePrivateConfig): Partial<PaymentConfig> => {
    // Encrypted data received, ignore any properties related to FreedomPay
    if (privateConfig?.keystore?.payment?.is_encrypted) {
      return {
        // Currently only FreedomPay is supported. If other payment types are added, this logic will need to be updated.
        type: PAYMENT_CONFIG_TYPES.FREEDOM_PAY,
        is_encrypted: privateConfig.keystore.payment.is_encrypted,
        data: privateConfig.keystore.payment.data,
        freedompay: {
          id: privateConfig.payment?.freedompay?.id,
          freedompay_store_id: undefined,
          freedompay_terminal_id: undefined,
        },
      };
    } else {
      // Unencrypted data received, use actual FreedomPay store/terminal IDs
      return {
        // Currently only FreedomPay is supported. If other payment types are added, this logic will need to be updated.
        type: PAYMENT_CONFIG_TYPES.FREEDOM_PAY,
        freedompay: {
          id: privateConfig.payment?.freedompay?.id,
          freedompay_store_id: privateConfig.keystore?.payment?.freedompay?.freedompay_store_id,
          freedompay_terminal_id:
            privateConfig.keystore?.payment?.freedompay?.freedompay_terminal_id,
        },
      };
    }
  };

  return {
    initialValues,
    hasPaymentConfig,
    paymentConfigTypes,
    getPaymentKeystoreConfig,
    mapPrivateConfigPayload,
    mapCentricHubPaymentPayload,
    clearCentricHubPaymentPayload,
  };
};

export default usePaymentInfo;
